import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useTranslation } from '@msdk/common'
import { ViewColumnDetail } from '@msdk/definitions'
import { ButtonIcon, COLORS, MenuItem, styled, TextField, Typography } from '@msdk/ui'
import React, { useMemo, useState } from 'react'

import { useDynamicViewContext } from '../context'
import { SetAsDefaultButton } from './SetAsDefaultButton'
import { SortableItem } from './SortableItem'

const ActionRow = styled(MenuItem)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  & button {
    width: fit-content;
  }

  &:hover {
    background-color: inherit;
  }
`

const FilterItem = styled(MenuItem)`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: flex-start;
  gap: 4px;
  pointer-events: auto;
  cursor: default;

  &:hover {
    background-color: inherit;
  }
  &:focus {
    background-color: inherit;
  }
  &:not(:focus) {
    background-color: inherit;
    box-shadow: unset;
  }
`

export function ManagementPanel() {
  const t = useTranslation()
  const { columns, updateColumns, showExtra, getColumnHeaderInfo } = useDynamicViewContext()
  const [searchTerm, setSearchTerm] = useState('')

  // isShowAll reflects the bulk-toggle state for columns with hidden === false.
  // The first non-hidden column is treated as always visible in this panel logic,
  // so isShowAll becomes false when any other non-hidden column is invisible (visibility === false).
  const [isShowAll, setShowAll] = useState<boolean>(() => {
    const firstVisibleCandidateIndex = columns.findIndex((column) => !column.hidden)
    const manageable = columns.filter((column) => !column.hidden)
    if (manageable.length === 0) return false
    return columns.every((column, i) => {
      if (column.hidden) return true
      if (i === firstVisibleCandidateIndex) return true
      return column.visibility !== false
    })
  })

  const filteredColumns = useMemo(() => {
    const indexToSkip = columns.findIndex((column) => !column.hidden)

    return columns
      .filter((_, i) => i !== indexToSkip)
      .filter((column) => {
        const searchInput = searchTerm.toLowerCase().trim()

        return (
          !column.hidden &&
          (column.detail !== ViewColumnDetail.Extra || (showExtra && column.detail === ViewColumnDetail.Extra)) &&
          (column.fieldName.toLowerCase().includes(searchInput) ||
            column.overrideDescription?.toLowerCase().includes(searchInput) ||
            getColumnHeaderInfo(column).title.toLowerCase().includes(searchInput))
        )
      })
  }, [columns, searchTerm])

  function sortColumns(event: DragEndEvent) {
    const { active, over } = event

    if (!active || !over || active.id === over.id) {
      return
    }

    const oldIndex = columns.findIndex((c) => c.id === active.id)
    const newIndex = columns.findIndex((c) => c.id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      console.warn('Could not find item indexes for reordering')
      return
    }

    const sortedItems = arrayMove(columns, oldIndex, newIndex)

    updateColumns(sortedItems)
  }

  function toggleAllColumns() {
    // Keep at least one visible column: the first non-hidden column is never hidden here.
    const firstNonHiddenIndex = columns.findIndex((column) => !column.hidden)
    const newColumns = columns.map((column, i) => {
      if (column.hidden) return column
      return {
        ...column,
        visibility: i === firstNonHiddenIndex ? true : !isShowAll,
      }
    })
    updateColumns(newColumns)
    setShowAll(!isShowAll)
  }

  /**
   * Define d&d library proprietary sensors for events initiation/drop.
   */
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  return (
    <div>
      <FilterItem disableRipple>
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="standard"
          label="Find"
          autoFocus
          onKeyDown={(e) => {
            e.stopPropagation()
          }}
          fullWidth
        />
      </FilterItem>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={sortColumns}
        modifiers={[restrictToParentElement]}
      >
        <SortableContext items={filteredColumns} strategy={verticalListSortingStrategy}>
          {filteredColumns.map((item) => (
            <SortableItem key={item.id} column={item} />
          ))}
        </SortableContext>
      </DndContext>
      <ActionRow disableRipple>
        <SetAsDefaultButton />
        <ButtonIcon onClick={toggleAllColumns}>
          <Typography variant="button" color={COLORS.primary.main}>
            {t('LBL_TABLE_MANAGEMENT_SHOW', 'Show')} / {t('LBL_TABLE_MANAGEMENT_HIDE_ALL', 'hide all')}
          </Typography>
        </ButtonIcon>
      </ActionRow>
    </div>
  )
}
